import { useId } from 'react';

import { styled } from '@classify/react-core-components/styles';
import Typography from '@classify/react-core-components/Typography';
import LinearProgress from '@classify/react-core-components/LinearProgress';
import InfoIcon from '@mui/icons-material/Info';

const defaultTranslations = {
  label: 'Your profile is complete at',
};

type Variant = 'inline' | 'block' | 'responsive';

export interface Props {
  completion: number;
  threshold?: number;
  variant?: Variant;
  hasInfoIcon?: boolean;
  translations?: {
    label: string;
  };
}

const Root = styled('div', {
  shouldForwardProp: (prop) => !['variant'].includes(prop as string),
})<{ variant: Variant }>(({ theme: { spacing, breakpoints }, variant }) => {
  const styles = {
    block: {
      gap: spacing(1),
      flexDirection: 'column',
      alignItems: 'flex-start',
    },
    inline: {
      gap: spacing(1),
      flexDirection: 'row',
      alignItems: 'center',
    },
    responsive: {
      flexDirection: 'column',
      gap: spacing(2),
      width: '100%',
      [breakpoints.up('md')]: {
        gap: spacing(1),
        flexDirection: 'row',
        alignItems: 'center',
      },
    },
  } as const;

  return {
    display: 'flex',
    flexGrow: 1,
    ...styles[variant],
  };
});

const TextContainer = styled('div')(({ theme: { spacing } }) => ({
  whiteSpace: 'nowrap',
  display: 'inline-flex',
  alignItems: 'center',
  gap: spacing(0.5),
}));

const ProgressBar = styled(LinearProgress, {
  shouldForwardProp: (prop) => !['hasError'].includes(prop as string),
})<{ hasError?: boolean }>(({ hasError, theme: { palette } }) => ({
  flexGrow: 1,
  width: '100%',
  '.MuiLinearProgress-bar': hasError
    ? {
        backgroundColor: palette.form.label.error,
      }
    : undefined,
}));

export const getCompletionPercentage = (completion: number) => `${Math.round(completion * 100)}%`;

const ProfileCompletion = ({
  completion,
  translations = defaultTranslations,
  threshold = 0.85,
  variant = 'responsive',
  hasInfoIcon,
}: Props) => {
  const displayCompletion = getCompletionPercentage(completion);
  const isBellowThreshold = Boolean(threshold && completion < threshold);
  const progressColor = isBellowThreshold ? 'form.label.error' : 'brand.main';
  const labelId = useId();
  return (
    <Root variant={variant}>
      <TextContainer id={labelId}>
        {hasInfoIcon && <InfoIcon sx={{ color: 'info.main' }} />}
        {translations.label && (
          <Typography component="span" color="text.onMain">
            {translations.label}{' '}
          </Typography>
        )}
        <Typography component="span" variant="h5" sx={{ color: progressColor }}>
          {displayCompletion}
        </Typography>
      </TextContainer>
      <ProgressBar
        value={completion}
        maxValue={1}
        size="large"
        shape="rounded"
        hasError={isBellowThreshold}
        aria-labelledby={labelId}
      />
    </Root>
  );
};

export default ProfileCompletion;
